import React, { useState, useEffect, useRef, useMemo } from 'react';
import CommentList from './CommentList';
import CommentForm from './CommentForm';
import baseService from 'services/BaseService';
import ReactSVG from 'react-svg';
import { withTranslation } from 'react-i18next';
import cx from 'classnames';
import Loader from "../Loader"
import SmartPortal from '../SmartPortal';
import { ModalWindow } from 'ui-core-dashboard';
import CommentEmailHistory from './CommentEmailHistory';
import { isEmpty } from 'lodash';
import { DISPATCH_CONFIG } from './dispatchConfig';
import './styles.scss';

const CommentSection = (props) => {

  const { id: objectId, objectType, t, onOpen, onClose, isDisabled, initCachedState, onStateUpdate, onCommentCountChange, onCountChange, isCommentFormLoader, isForcedClose } = props;

  const [comments, setComments] = useState([]); // Stores comments
  const [loading, setLoading] = useState(true); // Loading state for comments
  const [error, setError] = useState(null);     // Error state for comments
  const [filters, setFilters] = useState([]);   // Stores available filters from backend
  const [selectedFilter, setSelectedFilter] = useState(null); // Selected filter
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Dropdown open state

  const filterDropdownRef = useRef(null);

  const [dispatch, setDispatch] = useState(!isEmpty(initCachedState) && initCachedState.dispatch  ? initCachedState.dispatch : DISPATCH_CONFIG.init );

  const [emailHistory, setEmailHistory] = useState({
    opened: false,
    id: null
  });
  const openEmailHistory = id => setEmailHistory({ opened: true, id });
  const closeEmailHistory = () => setEmailHistory({ opened: false, id: null });

  const deleteComment = (comment) => {
    const params = {
      // path: `DELETE`,
      data: {
        commentId: comment.commentId,
        attachment: comment.attachments.map(file => file.id),
      },
      jsonType: true
    };

    const editingId = dispatch.comment && dispatch.comment.commentId;
    const isDeletingEditingComment = editingId && comment.commentId === editingId;

    // check if we are deleting currently opened/editing comment
    if (isDeletingEditingComment) {
      // set delete dispatch to properly remove files
      setDispatch(DISPATCH_CONFIG.delete);
    }

    baseService.delete("comments_new", params)
      .then((response) => {
        if (response.success) {
          // it was in old logic, do we need to set init dispatch on comment deletion?
          // if (isDeletingEditingComment) {
          //   setDispatch(DISPATCH_CONFIG.init);
          // }
          getComments()
        } else if (response.errorMessage) {
          console.error("Error deleting comment:", response.errorMessage);
        }
      })
      .catch((error) => {
        console.error("Error deleting comment catch:", error);
      })
}

  const getComments = () => {
    setLoading(true);
    setError(null);

    const params = {
      path: `${objectType}/${objectId}`,
      data: { filter: selectedFilter ? selectedFilter.toString() : null }
    };
    
    baseService.get("comments_new", params)
      .then((response) => {
        if (response.success && response.result) {
          const result = Array.isArray(response.result) ? response.result : [response.result];
          setComments(result);
          if (onCommentCountChange && typeof onCommentCountChange === 'function' && response.total !== null) {
            onCommentCountChange(response.total);
          }
          if (onCountChange && typeof onCountChange === 'function' && response.total !== null) {
            onCountChange(response.total);
          }
        } else {
          setComments([]); // Clear comments on failure
        }
      })
      .catch((error) => {
        console.error("getComments catch error", error);
        setError("Failed to load comments.");
      })
      .finally(() => {
        setLoading(false); // End loading
      });
   
  };

  const getFilters = () => {
    const params = { path: `${objectType}` };

    baseService.get("comments_new_filters", params)
      .then((response) => {
        if (response.success && response.result) {
          setFilters(response.result); 
          const defaultFilter = response.result.find(f => f.byDefault);
          if (defaultFilter && !selectedFilter) {
            setSelectedFilter(defaultFilter.code);
          }
        } else {
            console.log("getFilters response error");
          }
        })
      .catch((error) => {
            console.error("getFilters catch error", error);
        });
        
  };

  const handleFilterSelect = (filterCode, event) => {
    event.stopPropagation(); 
    setSelectedFilter(filterCode);
    setIsDropdownOpen(false);
  };
  
  const toggleDropdown = () => {
    setIsDropdownOpen(prevState => !prevState);
  };

  const handleExistingFileDelete = (fileId, commentId) => {
    const newComments = comments.map(comment => {
      if (comment.commentId === commentId) {
        return {
          ...comment,
          attachments: comment.attachments.filter(attachment => attachment.id !== fileId)
        }
      }
      return comment
    });
    setComments(newComments);
  }

  useEffect(() => {
    getFilters(); 
  }, []);
  
  useEffect(() => {
    if (selectedFilter !== null) {
      getComments();
    }
  }, [objectId, selectedFilter]); 

  useEffect(() => {
    if (isForcedClose) {
        setDispatch(DISPATCH_CONFIG.init);
    }
}, [isForcedClose]);
  
  return (
    <>
    <div className="comment-section">
      {/* <div className='comment-section__title'>Comments for {objectType} {objectId}</div> */}
      {filters && filters.length > 0 && (
      <div className="comment-section__header">
        {/* Custom Dropdown for Filter Selection */}
          <div 
            className="comment-section__filter-dropdown"
            onClick={toggleDropdown}
            role="button"
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            tabIndex={0}
          >
            
            <div className={cx("filter-dropdown__selected", {"filter-dropdown__selected--open": isDropdownOpen})} ref={filterDropdownRef}>
              {
                filters.length > 0 
                ? filters.find(f => f.code === selectedFilter)?.label 
                : "Select Filter"
              }
              <ReactSVG className="filter-dropdown__icon" src={`/data/svg/dropdown.svg`} />
            </div>
            {isDropdownOpen && (
              <SmartPortal
                portalClassName="portal-filter-dropdown"
                targetElement={filterDropdownRef && filterDropdownRef.current}
                portalPlacement="bottom inner-left"
                portalKey={objectId+1}
                onClickOutside={() => setIsDropdownOpen(false)}
                outerOffset={4}
              >
                <div className="filter-dropdown__options" role="listbox">
                  {filters.map((filter) => (
                    <div 
                      key={filter.code} 
                      className={`filter-dropdown__option ${filter.code === selectedFilter ? 'selected' : ''}`}
                      onClick={(e) => handleFilterSelect(filter.code, e)}
                      role="option"
                      aria-selected={filter.code === selectedFilter}
                      tabIndex={0}
                    >
                      {filter.label}
                    </div>
                  ))}
                </div>
              </SmartPortal>
            )}
          </div>

        <button
          type="button"
          className="comment-section__refresh-button"
          onClick={() => {
            getComments();
          }}
          aria-label="Refresh Comments"
        >
          <ReactSVG className="refresh-icon" src="/data/svg/refresh_comments.svg" /> 
        </button>
      </div>
      )}

      {/* Loading Indicator */}
      {loading && <Loader />}

      {/* Comments List */}
      {!loading && !error && (
        <CommentList 
        comments={comments}
        objectId={objectId} 
        objectType={objectType}
        openEmailHistory={openEmailHistory}
        setDispatch={setDispatch}
        isDisabled={isDisabled}
        onStateUpdate={onStateUpdate}
        onDeleteComment={deleteComment}
        {...props} 
        />
      )}
      {/* Comment Form */}
      <CommentForm 
        onCommentSubmitted={getComments} 
        objectId={objectId} 
        objectType={objectType}
        dispatch={dispatch}
        setDispatch={setDispatch}
        onOpen={onOpen}
        onClose={onClose}
        isDisabled={isDisabled}
        onStateUpdate={onStateUpdate}
        initCachedState={initCachedState}
        isCommentFormLoader={isCommentFormLoader}
        onExistingFileDelete={handleExistingFileDelete}
        {...props} 
      />

    </div>
    {emailHistory.opened &&
      <ModalWindow onClose={closeEmailHistory} className='ordering-component-ui-core-wrapper'>
          <header className='modal-window-header'>
              <div className='modal-window-title'>{t('commentEmailHistory')}</div>
          </header>
          <div className='order-comment-email-history-new'>
              <CommentEmailHistory entityId={emailHistory.id} />
          </div>
      </ModalWindow>
    }
    </>
  );
};

export default (withTranslation()(CommentSection));
